// Display mappings for Firestore data types
// Maps short format values to user-friendly display names

// ============================================================================
// INDUSTRY MAPPINGS
// ============================================================================

export const INDUSTRY_DISPLAY_MAP = {
  'adtech-media': 'AdTech / MarTech / Media',
  agritech: 'AgriTech',
  'big-data': 'AI / Big Data / Analytics',
  'consumer-goods': 'Consumer Goods / Lifestyle',
  cybersecurity: 'Cybersecurity / Privacy',
  'deep-tech': 'DeepTech / Emerging Technologies',
  'defence-tech': 'DefenceTech / Dual-Use Technologies',
  ecommerce: 'E-commerce / Marketplaces',
  edtech: 'EdTech',
  entertainment: 'Entertainment / Music / Gaming',
  fashion: 'Fashion / Beauty',
  fintech: 'FinTech / Financial Services',
  foodtech: 'FoodTech',
  greentech: 'GreenTech / ClimateTech / Energy',
  healthtech: 'Healthcare / MedTech / Life Sciences',
  hrtech: 'HRTech / Future of Work',
  impacttech: 'ImpactTech / Social Innovation',
  industrialtech: 'IndustrialTech / Robotics / Advanced Manufacturing',
  'it-software': 'IT / Software / SaaS',
  legaltech: 'LegalTech / RegTech',
  'transport-logistics': 'Mobility / Transportation / Logistics',
  proptech: 'PropTech / ConstructionTech',
  retail: 'Retail / Commerce',
  // No longer offered on its own. Older profiles still show the one industry name.
  software: 'IT / Software / SaaS',
  'space-tech': 'SpaceTech / Aerospace',
  'travel-hospitality': 'Travel / Hospitality',
  'water-circular': 'Water / Circular Economy',
  // No longer offered. Kept so older profiles still show the new name.
  'service-solutions': 'Travel / Hospitality',
  // No longer offered. Kept so investors who already chose it still see a name.
  'emerging-tech': 'Emerging Tech (NFTs, VR, Robotics)',
} as const;

// ============================================================================
// BUSINESS MODEL MAPPINGS
// ============================================================================

export const BUSINESS_MODEL_DISPLAY_MAP = {
  b2c: 'B2C',
  b2b: 'B2B',
  'b2b-b2c': 'B2B and B2C',
} as const;

// ============================================================================
// PRODUCT FOCUS MAPPINGS
// ============================================================================

export const PRODUCT_FOCUS_DISPLAY_MAP = {
  'software-saas': 'Software/SaaS',
  hardware: 'Hardware / physical products',
  services: 'Services',
  'platform-marketplace': 'Platform/Marketplace',
  'consumer-goods': 'Consumer Goods',
  other: 'Other',
} as const;

// ============================================================================
// STAGE MAPPINGS
// ============================================================================

export const STAGE_DISPLAY_MAP = {
  'early-seed': 'Early/Seed',
  growth: 'Growth',
  'series-a': 'Series A',
  'series-b-higher': 'Series B/Higher',
} as const;

// ============================================================================
// CURRENCY MAPPINGS
// ============================================================================

export const CURRENCY_DISPLAY_MAP = {
  eur: 'EUR',
  usd: 'USD',
  gbp: 'GBP',
  sek: 'SEK',
} as const;

// ============================================================================
// TRACTION MAPPINGS
// ============================================================================

export const TRACTION_DISPLAY_MAP = {
  'no-users': 'No users/clients yet',
  'no-users-clients-yet': 'No users/clients yet',
  'users-no-revenue': 'Users/Clients but no revenue',
  'users-clients-no-revenue': 'Users/Clients but no revenue',
  'revenue-exists': 'Revenue exists',
} as const;

// ============================================================================
// INVESTMENT TYPE MAPPINGS
// ============================================================================

export const INVESTMENT_TYPE_DISPLAY_MAP = {
  equity: 'Equity',
  'revenue-based': 'Revenue-based',
  'services-equity': 'Services in exchange for equity',
} as const;

// ============================================================================
// EXPERTISE MAPPINGS
// ============================================================================

export const EXPERTISE_DISPLAY_MAP = {
  'industries-expertise': "I/we offer expertise in the fields selected in the 'Industries' section",
  'industries-contacts': "I offer contacts in the fields selected in the 'Industries' section",
  other: 'Other',
} as const;

// ============================================================================
// SUBSCRIPTION MAPPINGS
// ============================================================================

export const SUBSCRIPTION_STATUS_DISPLAY_MAP = {
  active: 'Active',
  past_due: 'Past Due',
  cancelled: 'Cancelled',
  unpaid: 'Unpaid',
} as const;

export const SUBSCRIPTION_TYPE_DISPLAY_MAP = {
  annual: 'Annual',
  lifetime: 'Lifetime',
} as const;

// ============================================================================
// MATCH STATUS MAPPINGS
// ============================================================================

export const MATCH_STATUS_DISPLAY_MAP = {
  created: 'Created',
  pending: 'Pending',
  interested: 'Interested',
  archived: 'Archived',
  'not-interested': 'Not Interested',
} as const;

export const DEAL_STATUS_DISPLAY_MAP = {
  'not-contacted': 'Not contacted',
  favorited: 'Following',
  contacted: 'Contacted',
  met: 'Met with them',
  'deal-progress': 'Deal in Progress',
  'deal-signed': 'Deal Signed',
} as const;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get display name for any enum value
 */
export function getDisplayName<T extends Record<string, string>>(
  mapping: T,
  value: keyof T
): string {
  return mapping[value] || String(value);
}

/**
 * Get all available options for a mapping
 */
export function getOptions<T extends Record<string, string>>(
  mapping: T
): Array<{ value: keyof T; label: string }> {
  return Object.entries(mapping).map(([value, label]) => ({
    value: value as keyof T,
    label,
  }));
}
